/**
 * useDateKey — Returns today's date as "YYYY-MM-DD" and re-renders when
 * the day rolls over.
 *
 * Why this exists: many stats components memoize date ranges around
 * `todayStr()` but don't include it as a dep — so once mounted they freeze
 * on the day of mount. The TV slideshow gets away with this because its
 * per-slide `setInterval` re-renders the parent every 12s; the in-app
 * Stats tab does not, and the leaderboard / personal stats silently
 * exclude the user's own ascents after midnight (see commit history for
 * the gym-stats leaderboard regression).
 *
 * Pass the gym's IANA timezone so the boundary matches the same TZ used
 * when ascents are written (`todayStr(gymMeta?.timezone)` in
 * `useMainApp.toggleAscent`). Omit it for cross-gym views where there is
 * no single canonical timezone — the browser local then wins.
 *
 * Strategy: poll every 60 s and listen for `visibilitychange` so a tab
 * backgrounded over midnight catches up the moment the user returns.
 * State only flips when the value actually changes, so consumers using it
 * as a memo dep recompute exactly once per day boundary.
 */
import { useState, useEffect } from "react";
import { todayStr } from "../utils";

export function useDateKey(timezone) {
  const [key, setKey] = useState(() => todayStr(timezone));

  useEffect(() => {
    setKey(todayStr(timezone));

    const sync = () => {
      const next = todayStr(timezone);
      setKey(prev => (prev === next ? prev : next));
    };

    const intervalId = setInterval(sync, 60 * 1000);
    const onVisibility = () => { if (!document.hidden) sync(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [timezone]);

  return key;
}
